import { CustomerAuthenticationController } from "./components/customerauthentication.js";
import { SettingsController } from "./components/settings.js";
import { RegistrationController } from "./components/customerregistration.js";
import { ProfileManagementController } from "./components/profilemanagement.js";
import { RidecellCallback } from "./models/ridecellcallback.js";
import { MemoryCache } from "./inmemorycache.js";
import { Logger } from "./logging/logger.js";
import { EventController } from "./eventhub/eventcontroller.js";
import {
  EventType,
  EventName,
  RCEvent,
  EventData,
} from "./eventhub/eventmodels.js";
import { Util } from "./util.js";

export class CoreController {
  constructor() {
    this.customerAuthenticationController =
      new CustomerAuthenticationController();
    this.settingsController = new SettingsController();
    this.customerRegistrationController = new RegistrationController();
    this.customerProfileManagementController =
      new ProfileManagementController();
  }

  init(configs) {
    return new Promise((resolve, reject) => {
      if (
        configs.base_url &&
        configs.auth_token &&
        Util.isValidURL(configs.base_url)
      ) {
        this.saveConfigs(configs);

        const initCallback = new RidecellCallback();
        initCallback.onSuccess = (result) => {
          // Check if auth token exists (in case of logged in user)
          this.customerAuthenticationController
            .getAuthToken()
            .then((authToken) => {
              // If auth token exists in cache, refresh token
              this.customerAuthenticationController
                .reAuth(authToken)
                .then((result) => {
                  this.customerAuthenticationController
                    .getCustomer()
                    .then((result) => {
                      this.attachObservers();
                    })
                    .catch((error) => {
                      Logger.log("Error retrieving customer = ", error);
                    });
                  // Then load settings asynchronously. This will return to caller on success
                  resolve(this.settingsController.getSettings());
                })
                .catch((error) => {
                  // Even if we get a failure in loading the auth token we load the settings and return.
                  resolve(this.settingsController.getSettings());
                });
            })
            .catch((error) => {
              // Even if we get a failure in loading the auth token we load the settings and return.
              resolve(this.settingsController.getSettings());
            });
        };
        initCallback.onFailure = (error) => {
          reject(error);
        };
        EventController.addEvent(
          new RCEvent(
            EventName.INIT,
            new EventData(EventType.INIT),
            initCallback
          )
        );
      } else {
        reject(Util.getErrorJson("Invalid configs"));
      }
    });
  }

  /**
   * Saves configs to in memory cache.
   * @param {string} configs - Json string with tenant configs.
   */
  saveConfigs(configs) {
    /* In memory cache to lookup urls */
    MemoryCache.configs = configs;
  }

  /**
   * Attaches the observers for network requests.
   */
  attachObservers() {
    //CustomerAuthenticationManager.observeCustomer();
    //observeRental();
  }

  login({ email, password, deviceId }) {
    return this.customerAuthenticationController.login({
      email,
      password,
      deviceId,
    });
  }

  mfaVerify({ code, nonce }) {
    return this.customerAuthenticationController.mfaVerify({ code, nonce });
  }

  mfaResend({ nonce }) {
    return this.customerAuthenticationController.mfaResend({ nonce });
  }

  getCustomer({ fetchUpdatedCustomer = false }) {
    return new Promise((resolve, reject) => {
      if (!MemoryCache.authorizationToken) {
        reject(Util.getErrorJson("No logged in user"));
        return;
      }
      const haveCustomer =
        this.customerAuthenticationController.loggedInCustomer();
      if (haveCustomer && !fetchUpdatedCustomer)
        resolve(this.customerAuthenticationController.loggedInCustomer());
      else {
        resolve(this.customerAuthenticationController.getCustomer());
      }
    });
  }

  logout() {
    return this.customerAuthenticationController.logout();
  }

  resetPassword({ customerContact, isLeasingCustomer }) {
    return this.customerAuthenticationController.resetPassword({
      customerContact,
      isLeasingCustomer,
    });
  }

  getCachedCustomer() {
    return this.customerAuthenticationController.loggedInCustomer();
  }

  isRegistrationAllowed(lat, lng) {
    return this.customerRegistrationController.isRegistrationAllowed(lat, lng);
  }

  getConsentOptions(queryParams) {
    return this.customerRegistrationController.getConsentOptions(queryParams);
  }

  getPolicyDocs(queryParams) {
    return this.customerRegistrationController.getPolicyDocs(queryParams);
  }

  createCustomer(data) {
    return this.customerRegistrationController.createCustomer(data);
  }

  verifyPhoneNumber({ customerId, data }) {
    return this.customerRegistrationController.verifyPhoneNumber({
      customerId,
      data,
    });
  }

  getRegistrationSteps({ customerId, queryParams }) {
    return this.customerRegistrationController.getRegistrationSteps({
      customerId,
      queryParams,
    });
  }

  updateCustomer({ customerId, data }) {
    return this.customerRegistrationController.updateCustomer({
      customerId,
      data,
    });
  }

  getRegistrationInfo(lat, lng, customerId, product) {
    return this.customerRegistrationController.getRegistrationInfo(
      lat,
      lng,
      customerId,
      product
    );
  }

  saveMailingAddress({ customerId, data }) {
    return this.customerRegistrationController.saveMailingAddress({
      customerId,
      data,
    });
  }

  saveDriverLicense({ customerId, data }) {
    return this.customerRegistrationController.saveDriverLicense({
      customerId,
      data,
    });
  }

  uploadDocuments({ customerId, data }) {
    return this.customerRegistrationController.uploadDocuments({
      customerId,
      data,
    });
  }

  completeDocumentSubmission({ customerId, data }) {
    return this.customerRegistrationController.completeDocumentSubmission({
      customerId,
      data,
    });
  }

  resendVerificationCode({ customerId, data }) {
    return this.customerRegistrationController.resendVerificationCode({
      customerId,
      data,
    });
  }

  fetchPaymentSheet(data) {
    return this.customerRegistrationController.fetchPaymentSheet(data);
  }

  createConsent({ data, queryParams }) {
    return this.customerProfileManagementController.createConsent({
      data,
      queryParams,
    });
  }

  createPolicy({ data, queryParams }) {
    return this.customerProfileManagementController.createPolicy({
      data,
      queryParams,
    });
  }

  getActionRequiredPolicy(queryParams) {
    return this.customerProfileManagementController.getActionRequiredPolicy(
      queryParams
    );
  }

  linkBusinessProfile({ customerId, data }) {
    return this.customerProfileManagementController.linkBusinessProfile({
      customerId,
      data,
    });
  }

  getDocumentSpecificationForMIV() {
    return this.customerRegistrationController.getDocumentSpecificationForMIV();
  }

  deleteMailingAddress(id) {
    return this.customerProfileManagementController.deleteMailingAddress(id);
  }

  getCards(id) {
    return this.customerProfileManagementController.getCards(id);
  }

  deleteCard({ customerId, data }) {
    return this.customerProfileManagementController.deleteCard({
      customerId,
      data,
    });
  }

  markCardAsDefault({ customerId, data }) {
    return this.customerProfileManagementController.markCardAsDefault({
      customerId,
      data,
    });
  }

  getAxeptaHostedPage({ customerId, data }) {
    return this.customerProfileManagementController.getAxeptaHostedPage({
      customerId,
      data,
    });
  }

  createVeriffSession(id) {
    return this.customerRegistrationController.createVeriffSession(id);
  }

  getRegistrationStatus(id) {
    return this.customerRegistrationController.getRegistrationStatus(id);
  }

  resetPasswordVerify(data) {
    return this.customerAuthenticationController.resetPasswordVerify(data);
  }

  resetPasswordConfirm(data) {
    return this.customerAuthenticationController.resetPasswordConfirm(data);
  }
}
